import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthContext";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { saveAudience } from "@/lib/intel/feeds.functions";
import { WORKSPACES, WORKSPACE_IDS, type WorkspaceId } from "@/lib/workspace/workspaces";

export const Route = createFileRoute("/signup")({
  validateSearch: (s: Record<string, unknown>): { plan?: string } =>
    typeof s.plan === "string" ? { plan: s.plan } : {},
  head: () => ({ meta: [{ title: "Create account · CARIO INTEL" }] }),
  component: SignupPage,
});

function SignupPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/signup" });
  const { openCheckout } = usePaddleCheckout();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [audience, setAudience] = useState<WorkspaceId>("other");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (session) {
      // Persist audience on first sign-in after signup (best-effort).
      try {
        const pending = typeof window !== "undefined" ? window.sessionStorage.getItem("cario:audience") : null;
        if (pending) {
          saveAudience({ data: { audience: pending as any } }).catch(() => {}).finally(() => {
            window.sessionStorage.removeItem("cario:audience");
          });
        }
      } catch {}
      if (search.plan) {
        openCheckout({ priceId: search.plan, customerEmail: session.user.email ?? undefined, userId: session.user.id })
          .finally(() => navigate({ to: "/pricing" }));
      } else {
        navigate({ to: "/pricing" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      try { window.sessionStorage.setItem("cario:audience", audience); } catch {}
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { display_name: displayName || null, audience },
          emailRedirectTo: typeof window !== "undefined" ? window.location.origin : undefined,
        },
      });
      if (error) {
        if (/already registered|already exists/i.test(error.message)) {
          throw new Error("That email already has an account — sign in instead.");
        }
        throw error;
      }
      if (!data.session) {
        // Auto-confirm is on, so sign in straight away to start the trial flow.
        const { error: signInErr } = await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });
        if (signInErr) {
          toast.success("Account created. Confirm your email, then sign in.");
          return;
        }
      }
      toast.success("Account created — choose your plan to start the free trial.");
    } catch (e: any) {
      toast.error(e?.message || "Could not create account");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PaymentTestModeBanner />
      <Toaster />
      <div className="grid-bg flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="mb-6 flex items-center justify-between">
            <Link to="/"><Logo /></Link>
            <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan hover:text-foreground">
              Sign in →
            </Link>
          </div>
          <div className="glass-strong rounded-xl p-8 ring-glow">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Create operator account</p>
            <h1 className="mt-2 font-display text-3xl">Get on the platform</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {search.plan ? "Create your account — checkout opens right after." : "Free to register. Choose a plan to activate scans."}
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="display_name">Display name</Label>
                <Input id="display_name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={120} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="audience">I'm using CARIO as an…</Label>
                <select
                  id="audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value as any)}
                  className="w-full rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm outline-none"
                >
                  {WORKSPACE_IDS.map((id) => (
                    <option key={id} value={id}>
                      {WORKSPACES[id].label} — {WORKSPACES[id].blurb}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground">
                  This tailors your dashboard layout, tools and playbooks. You can change it any time in Settings.
                </p>
              </div>
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? "Creating account…" : "Create account"}
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-muted-foreground">
              Already have an account? <Link to="/login" className="text-cyan hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}